/**
 * ChipValuesInput - Entrada em massa de ICCIDs/linhas (colar lista ou importar arquivo).
 *
 * Usado na montagem do lote e na lista de exceções do retorno do broker, para que
 * nenhuma ação exija marcar chip por chip.
 *
 * RELEASE: 2026-10-14 v1.0.0 - Versão inicial.
 */
import { useRef, useState } from 'react';
import { FileUp, ListChecks, Loader2 } from 'lucide-react';
import * as XLSX from 'xlsx';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { CANCEL_MAX_CHIPS, parseChipValues } from '@/hooks/useStockChipCancellations';
import { toast } from 'sonner';

/** Lê a primeira coluna de um CSV/XLSX, ignorando cabeçalho textual. */
export async function readChipValuesFromFile(file: File): Promise<string[]> {
  const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, raw: false });
  const values = rows.map((r) => String((r as unknown[])[0] ?? '').trim()).filter(Boolean);
  // Cabeçalho (ex.: "ICCID") não tem dígitos suficientes
  if (values.length && !/\d{6,}/.test(values[0])) values.shift();
  return parseChipValues(values.join('\n'));
}

interface Props {
  /** Recebe os valores já separados e sem repetição */
  onSubmit: (values: string[]) => void | Promise<void>;
  busy?: boolean;
  submitLabel?: string;
  placeholder?: string;
}

export function ChipValuesInput({ onSubmit, busy, submitLabel = 'Validar e adicionar', placeholder }: Props) {
  const [text, setText] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const count = text ? parseChipValues(text).length : 0;

  const send = async (values: string[]) => {
    if (values.length === 0) return;
    if (values.length > CANCEL_MAX_CHIPS) {
      toast.error(`Máximo de ${CANCEL_MAX_CHIPS.toLocaleString('pt-BR')} chips por vez`);
      return;
    }
    await onSubmit(values);
    setText('');
  };

  return (
    <div className="space-y-2">
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, 200_000))}
        placeholder={placeholder ?? 'Cole aqui os ICCIDs ou linhas (um por linha, vírgula ou ponto e vírgula)'}
        className="min-h-[120px] font-mono text-xs"
        disabled={busy}
      />
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" onClick={() => send(parseChipValues(text))} disabled={busy || count === 0}>
          {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ListChecks className="mr-1.5 h-4 w-4" />}
          {submitLabel}{count ? ` (${count.toLocaleString('pt-BR')})` : ''}
        </Button>
        <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()}>
          <FileUp className="mr-1.5 h-4 w-4" /> Importar CSV/XLSX
        </Button>
        <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,.txt" className="hidden"
          onChange={async (e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (!f) return;
            if (f.size > 10 * 1024 * 1024) { toast.error('Arquivo acima de 10 MB'); return; }
            try { await send(await readChipValuesFromFile(f)); } catch { toast.error('Não foi possível ler o arquivo'); }
          }} />
        <span className="text-[11px] text-muted-foreground">Arquivo: ICCIDs na primeira coluna.</span>
      </div>
    </div>
  );
}
